<template>
  <v-form
    v-if="showStrapTable"
    ref="form"
    v-model="isValidationOk"
    lazy-validation>
    <v-container
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        wrap>
        <v-flex
          xs6
          md6>
          <v-btn
            :disabled="features.weightsMeasuresLock"
            small
            class="mx-0 font-weight-light"
            color="dover-blue"
            @click="onCopyStrap(0)">
            {{ $t('Copy from tank') }}
          </v-btn>
        </v-flex>
        <v-flex
          xs6
          md6
          text-xs-right>
          <v-btn
            :disabled="!isValidationOk"
            small
            class="mx-0 font-weight-light"
            color="dover-blue"
            @click="onCsvExport()">
            {{ $t('CSV export') }}
          </v-btn>
        </v-flex>
        <v-dialog
          v-model="selectTankDialog"
          :disabled="features.weightsMeasuresLock"
          persistent
          width="600px">
          <v-card>
            <v-card-title
              class="headline grey lighten-2"
              primary-title>
              {{ $t('Copy straptable from tank') }}
            </v-card-title>
            <v-card-text>
              <v-container
                py-0>
                <v-layout
                  wrap>
                  <v-flex
                    xs8
                    md8>
                    <v-select
                      v-model="tankToCopy"
                      :items="sourceTanks"
                      :label="`${ $t('Tank') }`"
                      item-value="tankNumber"
                      class="dover-input"
                      chips>
                      <template
                        slot="item"
                        slot-scope="data">
                        {{ $t('Tank {0}', [ data.item.tankNumber ]) }} - {{ data.item.tankDescription || $t('No name') }}
                      </template>
                      <template
                        slot="selection"
                        slot-scope="data">
                        <v-chip
                          :selected="data.selected"
                          :disabled="data.disabled"
                          :key="data.item.tankNumber"
                          class="v-chip--select-multi "
                          @input="data.parent.selectItem(data.item)">
                          <v-avatar class="dover-avatar "> {{ data.item.tankNumber }}</v-avatar>
                          {{ data.item.tankDescription || $t('Tank {0}', [ data.item.tankNumber ]) }}
                        </v-chip>
                      </template>
                    </v-select>
                  </v-flex>
                  <v-flex
                    xs2
                    md2
                    text-xs-right>
                    <v-btn
                      color="primary"
                      flat
                      @click="onCloseDialog(false)">
                      {{ $t('Cancel') }}
                    </v-btn>
                  </v-flex>
                  <v-flex
                    xs2
                    md2
                    text-xs-right>
                    <v-btn
                      color="secondary"
                      flat
                      @click="onCloseDialog(true)">
                      {{ $t('Confirm') }}
                    </v-btn>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-card-text>
          </v-card>
        </v-dialog>
        <v-flex
          xs12
          md12>
          <span
            class="caption grey--text font-weight-bold">
            {{ $t('Upload options') }}
          </span>
          <hr>
        </v-flex>
        <v-flex
          xs12
          md3>
          <span
            class="caption grey--text font-weight-bold">
            {{ $t('Level') }}
          </span>
          <hr>
          <v-radio-group
            v-model.number="tank.straptable.levelUnit"
            :disabled="features.weightsMeasuresLock"
            :row="true"
            @change="onUnitChange()">
            <v-radio
              v-for="(element, index) in options.levelUnits"
              :key="index"
              :label="element.desc"
              :value="element.code"/>
          </v-radio-group>
        </v-flex>
        <v-flex
          xs12
          md3>
          <span
            class="caption grey--text font-weight-bold">
            {{ $t('Volume') }}
          </span>
          <hr>
          <v-radio-group
            v-model.number="tank.straptable.volumeUnit"
            :disabled="features.weightsMeasuresLock"
            :row="true"
            @change="onUnitChange()">
            <v-radio
              v-for="(element, index) in options.volumeUnits"
              :key="index"
              :label="element.desc"
              :value="element.code"/>
          </v-radio-group>
        </v-flex>
        <v-flex
          v-if="shapeExpanded === null"
          xs12
          md3>
          <span
            class="caption grey--text font-weight-bold">
            {{ $t('Field separator') }}
          </span>
          <hr>
          <v-radio-group
            v-model.number="tank.straptable.fieldSeparator"
            :disabled="features.weightsMeasuresLock"
            :row="true">
            <v-radio
              v-for="(element, index) in options.fieldSeparators"
              :key="index"
              :label="fieldDesc[index]"
              :value="element.code"/>
          </v-radio-group>
        </v-flex>
        <v-flex
          v-if="shapeExpanded === null"
          xs12
          md3>
          <span
            class="caption grey--text font-weight-bold">
            {{ $t('File') }}
          </span>
          <hr>
          <v-text-field
            v-model="tank.straptable.filename"
            :disabled="features.weightsMeasuresLock"
            :label="`${ $t('Select strap table to upload') }`"
            prepend-icon="mdi-attachment"
            @click="onPickFile"/>
          <input
            ref="strapFile"
            type="file"
            style="display: none"
            accept="txt/csv"
            @change="onFilePicked">
        </v-flex>
        <v-flex
          xs12
          md10/>
        <v-expansion-panel
          v-model="shapeExpanded">
          <v-expansion-panel-content >
            <template v-slot:header>
              <div>{{ $t('Tank chart generator') }}</div>
            </template>
            <v-card>
              <v-card-text>
                <v-container
                  fill-height
                  fluid
                  grid-list-xl>
                  <v-layout
                    wrap>
                    <v-flex
                      xs6
                      md3>
                      <v-select
                        v-model="shapeSetup.shapeType"
                        :disabled="features.weightsMeasuresLock"
                        :items="shapeTypes"
                        :label="`${ $t('Shape') }`"
                        item-text="desc"
                        item-value="code"
                        class="dover-input"
                        chips
                        @change="onShapeChange()">
                        <template
                          slot="selection"
                          slot-scope="data">
                          <v-chip
                            :selected="data.selected"
                            :disabled="data.disabled"
                            :key="data.item.code"
                            class="v-chip--select-multi "
                            @input="data.parent.selectItem(data.item)">
                            <v-avatar class="dover-avatar ">{{ data.item.code }}</v-avatar>
                            {{ data.item.desc }}
                          </v-chip>
                        </template>
                      </v-select>
                    </v-flex>
                    <v-flex
                      xs6
                      md2>
                      <v-text-field
                        v-model.number="shapeSetup.height"
                        :disabled="features.weightsMeasuresLock"
                        :counter="7"
                        :rules="rules.required"
                        :label="`${ $t('Height') + ' (' + [ options.levelUnits[tank.straptable.levelUnit || 0].desc ] + ')' }`"
                        class="dover-input"
                        @change="onShapeChange()" />
                    </v-flex>
                    <v-flex
                      xs6
                      md2>
                      <v-text-field
                        v-model.number="shapeSetup.capacity"
                        :disabled="features.weightsMeasuresLock"
                        :counter="9"
                        :rules="rules.required"
                        :label="`${ $t('Capacity') + ' (' + [ options.volumeUnits[tank.straptable.volumeUnit || 0].desc ] + ')' }`"
                        class="dover-input"
                        @change="onShapeChange()" />
                    </v-flex>
                    <v-flex
                      v-if="shapeSetup.shapeType===4"
                      xs6
                      md2>
                      <v-text-field
                        v-model.number="shapeSetup.dishedRadius"
                        :disabled="features.weightsMeasuresLock"
                        :counter="7"
                        :rules="rules.required"
                        :label="`${ $t('Dished radius') + ' (' + [ options.levelUnits[tank.straptable.levelUnit || 0].desc ] + ')' }`"
                        class="dover-input"
                        @change="onShapeChange()" />
                    </v-flex>
                    <v-flex
                      xs6
                      md2>
                      <v-text-field
                        v-model.number="shapeSetup.step"
                        :disabled="features.weightsMeasuresLock"
                        :counter="6"
                        :rules="rules.required"
                        :label="`${ $t('Step') + ' (' + [ options.levelUnits[tank.straptable.levelUnit || 0].desc ] + ')' }`"
                        class="dover-input"
                        @change="onShapeChange()" />
                    </v-flex>
                  </v-layout>
                </v-container>
                <v-container
                  fill-height
                  fluid
                  grid-list-xl>
                  <v-layout justify-row-right>
                    <v-flex
                      xs12
                      md9>
                      <v-img
                        :src="shapeImage"
                        contain
                        max-height="300"/>
                    </v-flex>
                    <v-flex
                      xs12
                      md3
                      lg3
                      text-xs-right>
                      <v-btn
                        :disabled="features.weightsMeasuresLock || !shapeValid"
                        small
                        class="mx-0 font-weight-light"
                        color="dover-blue"
                        @click="onGenerate()">
                        {{ $t('Generate') }}
                      </v-btn>
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-flex
          xs12
          md12>
          <span
            class="caption grey--text font-weight-bold">
            {{ $t('Current table') }}
          </span>
          <hr>
        </v-flex>
        <v-flex
          xs12
          md12>
          <table
            border="0"
            width="100%">
            <tr>
              <th class="column">{{ $t('Index') }}</th>
              <th
                v-if="tank.straptable.levelUnit!==0"
                class="column">{{ $t('Level ({0})', [ options.levelUnits[tank.straptable.levelUnit].desc ]) }} </th>
              <th
                v-if="tank.straptable.volumeUnit!==0"
                class="column">{{ $t('Volume ({0})', [ options.volumeUnits[tank.straptable.volumeUnit].desc ]) }}  </th>
              <th
                v-if="tank.straptable.volumeUnit!==0"
                class="column">{{ $t('Diff ({0})', [ options.volumeUnits[tank.straptable.volumeUnit].desc ]) }}  </th>
              <th class="column">{{ $t('Level ({0})', [ $t('mm') ]) }} </th>
              <th class="column">{{ $t('Volume ({0})', [ $t('Litres').toLowerCase() ]) }} </th>
              <th class="column">{{ $t('Diff ({0})', [ $t('Litres').toLowerCase() ]) }} </th>
            </tr>
          </table>
          <virtual-list
            :size="size"
            :remain="remain"
            :bench="30"
            :start="start"
            :item="item"
            :itemcount="itemCount"
            :itemprops="getItemProps"
            class="list"/>
        </v-flex>
        <v-flex
          xs12
          md12
          lg12
          text-xs-center>
          <apexchart
            :options="lineChart.options"
            :series="lineChart.series"
            height= "450"
            width= "100%"
            type= "line" />
        </v-flex>
        <v-flex
          xs12
          md12
          text-xs-left>
          <v-btn-toggle
            v-model="graphType"
            dence
            group
            mandatory
            @change="onChangeGraphType(0)">
            <v-btn
              text
              small
              class="mx-0 font-weight-light"
              color="dover-blue"
              value = "0">
              {{ $t('Volume') }}
            </v-btn>
            <v-btn
              text
              small
              class="mx-0 font-weight-light"
              color="dover-blue"
              value = "1">
              {{ $t('Distribution') }}
            </v-btn>
          </v-btn-toggle>
        </v-flex>
        <v-flex
          xs12
          md12>
          <hr>
        </v-flex>
        <v-flex
          xs12
          md12
          text-xs-right>
          <v-btn
            :disabled="!isValidationOk || !uploading || features.weightsMeasuresLock"
            small
            class="mx-0 font-weight-light"
            color="dover-blue"
            @click="save">
            {{ $t('Save') }}
          </v-btn>
        </v-flex>
      </v-layout>
    </v-container>
  </v-form>
</template>

<script>
import TankStraptableDetailItem from './TankStraptableDetailItem.vue'
import virtualList from 'vue-virtual-scroll-list'
import Vue from 'vue'
import { i18n } from '@/i18n'

export default {
  components: { 'virtual-list': virtualList },
  props: {
    showStrapTable: {
      type: Boolean,
      required: true,
      default: false
    },
    tank: {
      type: Object,
      required: true,
      default: () => {}
    },
    tanks: {
      type: Array,
      required: true,
      default: () => []
    },
    systemSettings: {
      type: Object,
      required: true,
      default: () => {}
    },
    options: {
      type: Object,
      required: true,
      default: () => {}
    },
    features: {
      type: Object,
      required: true,
      default: () => {}
    }
  },
  data: () => ({
    selectTankDialog: false,
    isValidationOk: true,
    uploading: false,
    tankToCopy: -1,
    remain: 10,
    start: 0,
    size: 30,
    fieldDesc: [ i18n.t('Comma'), i18n.t('Semicolon'), i18n.t('Tab'), i18n.t('Space') ],
    item: TankStraptableDetailItem,
    shapeExpanded: null,
    shapeValid: false,
    maxStraps: 1000,
    shapeSetup: {
      shapeType: 0,
      height: 0.0,
      capacity: 0.0,
      dishedRadius: 0.0,
      step: 10.0
    },
    shapeTypes: [],
    shapeImage: '',
    rules: {
      required: [ v => Vue.prototype.Maglink.RulesRequired(v) ]
    },
    dataSelection: [ [0] ],
    rawDataSource: [],
    graphType: '1',
    graphConfig: [],
    lineChart: {
      series: [{
        name: '',
        data: []
      }],
      options: {
        noData: {
          text: i18n.t('Loading...'),
          style: {
            fontSize: '26px'
          }
        },
        xaxis: {
          type: 'number'
        },
        tooltip: {
          x: {
            formatter: function (val) {
              let dt = new Date(val)
              return dt.toISOString().substr(11, 5)
            }
          }
        },
        title: {
          text: '',
          align: 'center'
        },
        yaxis: {
          title: {
            text: ''
          }
        }
      }
    }
  }),
  computed: {
    sourceTanks () {
      return this.tanks.filter((value, index, arr) => (value.tankNumber !== this.tank.tankNumber))
    },
    itemCount () {
      return this.tank.straptable.details.length || 0
    }
  },
  watch: {
    shapeExpanded: {
      handler: function (newState, oldState) {
        this.shapeValid = this.isShapeValid()
      }
    }
  },
  mounted () {
    this.tank.straptable.fieldSeparator = 1
    this.tank.straptable.errorLineNumber = -1

    this.shapeTypes.push({ code: 1, desc: this.$t('Vertical'), image: './img/tank/tank_vertical.png' })
    this.shapeTypes.push({ code: 2, desc: this.$t('Flat-Ended'), image: './img/tank/tank_flat.png' })
    this.shapeTypes.push({ code: 3, desc: this.$t('Cylindrical'), image: './img/tank/tank_cylindrical.png' })
    // this.shapeTypes.push({ code: 4, desc: this.$t('Dished-Spherical'), image: './img/tank/tank_dished.png' })
    this.shapeTypes.push({ code: 5, desc: this.$t('Dished-Ellipsoidal'), image: './img/tank/tank_ellipsoidal.png' })
    this.initShapeSetup()
    this.loadGraphConfig()
    this.fillGraph()
  },
  methods: {
    getItemProps (itemIndex) {
      return {
        key: itemIndex,
        props: {
          levelUnit: this.tank.straptable.levelUnit,
          volumeUnit: this.tank.straptable.volumeUnit,
          height: 30,
          index: itemIndex,
          backgroundColor: (this.tank.straptable.errorLineNumber >= 0 && (itemIndex + 1) === this.tank.straptable.errorLineNumber) ? '#ffdddd ' : itemIndex % 2 === 0 ? '#cfdbfb' : 'transparent',
          data: this.tank.straptable.details[itemIndex] || {}
        }
      }
    },
    onUnitChange () {
      this.initShapeSetup()
      this.tank.straptable.details = []
      this.loadGraphConfig()
      this.fillGraph()
    },
    onCsvExport () {
      let sep = ','
      let fileContent = ''

      let strap = this.tank.straptable.details
      strap.forEach(item => {
        fileContent += item.levelImportUnit + sep
        fileContent += item.volumeImportUnit + '\n'
      })

      let filename = '{0} - {1}_{2}_{3}.csv'
      filename = filename.replace('{0}', this.$t('Tank {0}', [ this.tank.tankNumber ]))
      filename = filename.replace('{1}', this.$t('Straptable'))
      filename = filename.replace('{2}', this.options.levelUnits[this.tank.straptable.levelUnit].desc)
      filename = filename.replace('{3}', this.options.volumeUnits[this.tank.straptable.volumeUnit].desc)

      const link = this.Maglink.GetDownloadLinkCSV(fileContent, filename)
      document.body.appendChild(link)
      link.click()
    },
    onCloseDialog (confirm) {
      this.selectTankDialog = false
      if (!confirm || this.tankToCopy === -1) {
        return
      }
      // copy
      for (var i = 0; i < this.tanks.length; i++) {
        if (this.tanks[i].tankNumber === this.tankToCopy) {
          this.tank.straptable.levelUnit = this.tanks[i].straptable.levelUnit
          this.tank.straptable.volumeUnit = this.tanks[i].straptable.volumeUnit
          this.tank.straptable.fieldSeparator = this.tanks[i].straptable.fieldSeparator
          this.tank.straptable.filename = this.tanks[i].straptable.filename
          this.tank.straptable.details = this.tanks[i].straptable.details
          break
        }
      }
      this.$emit('tankStrapCopied', this.tank.tankNumber)
      this.uploading = true
    },
    onCopyStrap (index) {
      this.tankToCopy = -1
      this.selectTankDialog = true
    },
    onPickFile () {
      this.$refs.strapFile.click()
    },
    onFileLoaded (e) {
      var fileRows = e.target.result.split('\n')
      var precVolImportUnit = 0
      var separator = [',', ';', '\t', ' '][this.tank.straptable.fieldSeparator]

      this.tank.straptable.errorLineNumber = -1 // clear error line number

      this.tank.straptable.details = []
      for (var i = 0; i < fileRows.length; i++) {
        var row = fileRows[i]
        if (row.trim() === '') { continue }
        var items = row.split(separator)
        if (items.length < 2) { continue }
        var level = parseFloat(separator !== ',' ? items[0].replace(',', '.') : items[0])
        var volume = parseFloat(separator !== ',' ? items[1].replace(',', '.') : items[1])
        var newRow = {
          levelImportUnit: level,
          volumeImportUnit: volume,
          diffImportUnit: volume - precVolImportUnit,
          levelMm: this.Maglink.LevelFromDisplay(level, this.tank.straptable.levelUnit),
          volumeLt: this.Maglink.VolumeFromDisplay(volume, this.tank.straptable.volumeUnit),
          diffLt: this.Maglink.VolumeFromDisplay(volume - precVolImportUnit, this.tank.straptable.volumeUnit)
        }
        this.tank.straptable.details.push(newRow)
        precVolImportUnit = volume
      }
      this.uploading = true
      this.fillGraph()
    },
    onFilePicked (e) {
      const files = e.target.files

      if (files[0] !== undefined) {
        this.tank.straptable.filename = files[0].name
        var fr = new FileReader()
        fr.onloadend = this.onFileLoaded
        fr.readAsText(files[0])
      } else {
        this.tank.straptable.filename = ''
      }
    },
    level2VolumeStraight (height, capacity, level) {
      if (level <= 0.0) {
        return 0.0
      }
      if (level >= height) {
        return capacity
      }
      return (level / height * capacity)
    },
    level2VolumeFlat (height, capacity, level) {
      if (level <= 0.0) {
        return 0.0
      }
      if (level >= height) {
        return capacity
      }
      let radius = height / 2.0
      var volume = (radius - level) / radius
      volume = (Math.acos(volume) - volume * (Math.sqrt(1.0 - volume * volume))) / Math.PI
      volume = volume * capacity
      return volume
    },
    level2VolumeCylindrical (height, capacity, level) {
      if (level <= 0.0) {
        return 0.0
      }
      if (level >= height) {
        return capacity
      }
      let radius = height / 2.0
      let r3 = radius * radius * radius / 1000000.0
      let cylinderVolume = capacity - 4.0 / 3.0 * Math.PI * r3
      if (cylinderVolume < 0.0) {
        return 0.0
      }
      var volume = (radius - level) / radius
      volume = (Math.acos(volume) - volume * (Math.sqrt(1.0 - volume * volume))) / Math.PI
      volume = volume * cylinderVolume + 1.0 / 3.0 * Math.PI / 1000000.0 * level * level * (3.0 * radius - level)
      return volume
    },
    level2VolumeEllipsoidal (height, capacity, level) {
      if (level <= 0.0) {
        return 0.0
      }
      if (level >= height) {
        return capacity
      }
      let d3 = height * height * height / 1000000.0
      let headsVolume = Math.PI * d3 / 12.0
      if (headsVolume < 0.0) {
        return 0.0
      }

      let radius = height / 2.0
      var volume = (radius - level) / radius
      volume = (Math.acos(volume) - volume * (Math.sqrt(1.0 - volume * volume))) / Math.PI
      volume = volume * (capacity - headsVolume)

      var part = level / height
      var dished = (Math.PI * d3 / 12.0) * (3.0 * part * part - 2.0 * part * part * part)

      return volume + dished
    },
    initShapeSetup () {
      var maxLevel = this.Maglink.LevelFromDisplay(this.tank.maxLevel, this.systemSettings.levelUnit)
      var maxVolume = this.Maglink.VolumeFromDisplay(this.tank.maxVolume, this.systemSettings.volumeUnit)
      this.shapeSetup.height = this.Maglink.LevelToDisplay(maxLevel, this.tank.straptable.levelUnit)
      this.shapeSetup.capacity = this.Maglink.VolumeToDisplay(maxVolume, this.tank.straptable.volumeUnit)
      this.shapeSetup.step = this.Maglink.RoundFormat(this.Maglink.LevelToDisplay(maxLevel / 100, this.tank.straptable.levelUnit), 0)
      if (this.tank.straptable.shapeType !== undefined && this.tank.straptable.shapeType !== 0) {
        this.shapeSetup.shapeType = this.tank.straptable.shapeType
        this.shapeSetup.height = this.Maglink.LevelToDisplay(this.tank.straptable.height, this.tank.straptable.levelUnit)
        this.shapeSetup.capacity = this.Maglink.VolumeToDisplay(this.tank.straptable.capacity, this.tank.straptable.volumeUnit)
        this.shapeSetup.dishedRadius = this.Maglink.LevelToDisplay(this.tank.straptable.dishedRadius, this.tank.straptable.levelUnit)
        this.shapeSetup.step = this.Maglink.LevelToDisplay(this.tank.straptable.step, this.tank.straptable.levelUnit)
      }
    },
    onShapeChange () {
      this.shapeValid = this.isShapeValid()
      var isImage = false
      for (var i = 0; i < this.shapeTypes.length; i++) {
        if (this.shapeTypes[i].code === this.shapeSetup.shapeType) {
          this.shapeImage = this.shapeTypes[i].image
          isImage = true
        }
      }
      if (!isImage) {
        this.shapeImage = ''
      }
    },
    onGenerate (e) {
      if (!this.isShapeValid()) {
        return
      }
      this.tank.straptable.shapeType = this.shapeSetup.shapeType
      this.tank.straptable.height = this.Maglink.LevelFromDisplay(this.shapeSetup.height, this.tank.straptable.levelUnit)
      this.tank.straptable.capacity = this.Maglink.VolumeFromDisplay(this.shapeSetup.capacity, this.tank.straptable.volumeUnit)
      this.tank.straptable.dishedRadius = this.Maglink.LevelFromDisplay(this.shapeSetup.dishedRadius, this.tank.straptable.levelUnit)
      this.tank.straptable.step = this.Maglink.LevelFromDisplay(this.shapeSetup.step, this.tank.straptable.levelUnit)

      this.tank.straptable.details = []

      let height = this.tank.straptable.height * 1.0
      let capacity = this.tank.straptable.capacity * 1.0
      var level = 0.0
      var volume = 0.0
      var prevVolume = 0.0
      var i = 0
      while (i <= this.maxStraps) {
        i += 1
        level = this.Maglink.RoundFormat(level, 2) * 1.0
        switch (this.tank.straptable.shapeType) {
          case 1: volume = this.level2VolumeStraight(height, capacity, level); break
          case 2: volume = this.level2VolumeFlat(height, capacity, level); break
          case 3: volume = this.level2VolumeCylindrical(height, capacity, level); break
          // case 4: volume = this.level2VolumeDished(height, capacity, level); break
          case 5: volume = this.level2VolumeEllipsoidal(height, capacity, level); break
          default: volume = 0.0
        }
        volume = this.Maglink.RoundFormat(volume, 2) * 1.0
        if (level > 0.001 && volume < 0.001) {
          break
        }
        var newRow = {
          levelImportUnit: this.Maglink.LevelToDisplay(level, this.tank.straptable.levelUnit) * 1.0,
          volumeImportUnit: this.Maglink.VolumeToDisplay(volume, this.tank.straptable.volumeUnit) * 1.0,
          diffImportUnit: this.Maglink.VolumeToDisplay(volume - prevVolume, this.tank.straptable.volumeUnit) * 1.0,
          levelMm: level,
          volumeLt: volume,
          diffLt: this.Maglink.RoundFormat(volume - prevVolume, 2) * 1.0
        }
        this.tank.straptable.details.push(newRow)
        prevVolume = volume

        level += this.tank.straptable.step * 1.0
        if (level >= this.tank.straptable.height * 1.0) {
          level = this.tank.straptable.height * 1.0
          if (i < this.maxStraps) {
            i = this.maxStraps // create last strap at the top of the tank
          }
        }
      }
      if (this.tank.straptable.details.length > 1) {
        this.tank.straptable.filename = this.$t('Tank chart generator')
        this.uploading = true
      }
      this.fillGraph()
    },
    isShapeValid () {
      if (this.shapeSetup.shapeType === 0) {
        return false
      }
      if (this.shapeSetup.height * 1.0 < 1.0 || this.shapeSetup.capacity * 1.0 < 1.0) {
        return false
      }
      if ((this.shapeSetup.height * 1.0) / (this.shapeSetup.step * 1.0) > this.maxStraps) {
        return false
      }
      if ((this.shapeSetup.shapeType === 4) && (this.shapeSetup.dishedRadius * 1.0) < (this.shapeSetup.height / 2.0)) {
        return false
      }
      return true
    },
    save () {
      if (this.$refs.form.validate()) {
        this.isValidationOk = true
        this.$emit('tankStrapSaved', this.tank.tankNumber)
      } else {
        this.isValidationOk = false
      }
    },
    onChangeGraphType () {
      this.loadGraphConfig()
      this.fillGraph()
    },
    loadGraphConfig () {
      let volUdm = this.options.volumeUnits[this.tank.straptable.volumeUnit || 0].desc
      let levelUdm = this.options.levelUnits[this.tank.straptable.levelUnit || 0].desc
      this.graphConfig = [
        {
          index: 0,
          desc: this.$t('Volume'),
          key: 'volumeImportUnit',
          isRef: true,
          group: 'vol',
          yValue: [],
          yTooltipTitle: this.graphType === '1' ? this.$t('Distribution') + ' (' + volUdm + ' / ' + levelUdm + ')' : this.$t('Volume') + ' (' + volUdm + ')',
          yTitle: this.graphType === '1' ? volUdm + ' / ' + levelUdm : volUdm,
          xTitle: levelUdm,
          formatter: function (list) {
            let res = []
            for (let index = 0; index < list.length; index++) {
              res.push(Vue.prototype.Maglink.VolumeFormat(list[index]) * 1)
            }
            return res
          }
        }
      ]
    },
    fillGraph () {
      let levelUdm = this.options.levelUnits[this.tank.straptable.levelUnit || 0].desc
      this.rawDataSource = this.tank.straptable.details

      // x-axis
      let xValues = this.rawDataSource.map(a => a.levelImportUnit)
      let series = []
      let columnDesc = []
      let yTitle = ''
      let xTitle = ''

      // column selected
      let selection = this.dataSelection[0]
      for (let index = 0; index < selection.length; index++) {
        // current column index
        let itemIndex = selection[index]
        // current column config
        let currConfig = this.graphConfig[itemIndex]

        // it's the same for each comm element
        yTitle = currConfig.yTitle
        xTitle = currConfig.xTitle
        columnDesc.push(currConfig.desc)
        // get y values
        currConfig.yValue = currConfig.formatter(this.rawDataSource.map(a => a[currConfig.key]))

        // must combine x value with y values
        let yValue = []
        if (currConfig.yValue.length < 3) { // for undefined strapping table
          let item = []
          item.push(0.0)
          if (this.graphType === '1' || currConfig.yValue.length < 2 || currConfig.yValue[1] < 0.01) {
            item.push(0.0)
          } else {
            item.push(Vue.prototype.Maglink.VolumeFormat((currConfig.yValue[1]) / (xValues[1])))
          }
          yValue.push(item)
        }
        for (let count = 1; count < currConfig.yValue.length; count++) {
          let item = []
          item.push(xValues[count])
          if (this.graphType !== '1') {
            item.push(Vue.prototype.Maglink.VolumeFormat(currConfig.yValue[count]))
          } else {
            if (xValues[count] - xValues[count - 1] > 0.0) {
              item.push(Vue.prototype.Maglink.VolumeFormat((currConfig.yValue[count] - currConfig.yValue[count - 1]) / (xValues[count] - xValues[count - 1])))
            } else {
              item.push(0.0)
            }
          }
          yValue.push(item)
        }
        series.push({
          name: currConfig.yTooltipTitle,
          data: yValue
        })
      }

      let xLabel = this.$t('Level') + ' (' + levelUdm + '): '
      let reportTitle = this.$t('Straptable') + ': '
      reportTitle += this.tank.tankDescription

      this.lineChart = {
        series: series,
        options: {
          noData: {
            text: this.$t('Loading...')
          },
          xaxis: {
            type: 'number',
            hideOverlappingLabels: true,
            tickAmount: 20,
            title: {
              text: xTitle
            }
          },
          tooltip: {
            x: {
              formatter: function (val) {
                return xLabel + Vue.prototype.Maglink.VolumeFormat(val)
              }
            }
          },
          title: {
            text: reportTitle,
            align: 'center'
          },
          yaxis: {
            title: {
              text: yTitle
            },
            forceNiceScale: true
          },
          chart: {
            zoom: {
              enabled: true,
              type: 'xy'
            }
          }
        }
      }
    }
  }
}
</script>

<style scoped>
.scroller {
  table-layout: fixed;
  height: 300px;
  max-height: 300px;
  overflow: auto;
}
.list {
    background-color: #ffffff;
    -webkit-overflow-scrolling: touch;
}
.column {
  width: 13%;
  display: inline-block;
  text-align: right;
  font-weight: bold;
  color: gray;
}
</style>
